import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { defaultYear } from '../../common/constant';
import { receive_numeric1d, request_imgfile1d_generation } from '../../common/receive'; // @todo
import { RootState } from '../../rootReducer';
import { AppThunk } from '../../store';
// @todo Consider if it is reasonable to import other slices.
import { selectDatabase, selectEnsemble, selectExperiment, selectFrequency, selectModel, selectSingleVariable } from '../fileRepertoire/fileRepertoireSlice';

interface Viewer1dOption {
  // Spatial domain.
  lat: number;
  lon: number;

  // XY.
  xMax: number;
  xMin: number;
  yMax?: number;
  yMin?: number;
  yRangeEnabled: boolean;

  // Misc.
  imageSize: 'normal' | 'wide' | 'ultrawide';
  periodAverageEnabled: boolean;
  unit: string;

  // Post.
  error?: string;
  image?: Blob;
  isImageLoading: boolean;
  numerical?: string[][];
  isNumericalLoading: boolean;
}

const initialState: Viewer1dOption = {
  // Spatial domain.
  lat: -90,
  lon: -180,

  // XY.
  xMax: defaultYear,
  xMin: defaultYear,
  yRangeEnabled: false,

  // Misc.
  imageSize: 'normal',
  periodAverageEnabled: false,
  unit: 'original',

  // Post.
  isImageLoading: false,
  isNumericalLoading: false,
};

const viewer1dOptionSlice = createSlice({
  name: 'viewer1dOption',
  initialState,
  reducers: {
    // Spatial domain.
    updateLat(state, action: PayloadAction<{lat: Viewer1dOption['lat']}>) {
      const { lat } = action.payload;
      state.lat = lat;
    },
    updateLon(state, action: PayloadAction<{lon: Viewer1dOption['lon']}>) {
      const { lon } = action.payload;
      state.lon = lon;
    },

    // XY.
    updateXMin(state, action: PayloadAction<{xMin: Viewer1dOption['xMin']}>) {
      const { xMin } = action.payload;
      state.xMin = xMin;
    },
    updateXMax(state, action: PayloadAction<{xMax: Viewer1dOption['xMax']}>) {
      const { xMax } = action.payload;
      state.xMax = xMax;
    },
    updateYMin(state, action: PayloadAction<{yMin: Viewer1dOption['yMin']}>) {
      const { yMin } = action.payload;
      state.yMin = yMin;
    },
    updateYMax(state, action: PayloadAction<{yMax: Viewer1dOption['yMax']}>) {
      const { yMax } = action.payload;
      state.yMax = yMax;
    },
    updateYRangeEnabled(state, action: PayloadAction<{yRangeEnabled: Viewer1dOption['yRangeEnabled']}>) {
      const { yRangeEnabled } = action.payload;
      state.yRangeEnabled = yRangeEnabled;
    },

    // Misc.
    updateImageSize(state, action: PayloadAction<{imageSize: Viewer1dOption['imageSize']}>) {
      const { imageSize } = action.payload;
      state.imageSize = imageSize;
    },
    updatePeriodAverageEnabled(state, action: PayloadAction<{periodAverageEnabled: Viewer1dOption['periodAverageEnabled']}>) {
      const { periodAverageEnabled } = action.payload;
      state.periodAverageEnabled = periodAverageEnabled;
    },
    updateUnit(state, action: PayloadAction<{unit: Viewer1dOption['unit']}>) {
      const { unit } = action.payload;
      state.unit = unit;
    },

    // Post.
    getImage1dStart(state) {
      state.error = undefined;
      state.isImageLoading = true;
    },
    getImage1dSuccess(state, action: PayloadAction<{image: Blob}>) {
      const { image } = action.payload;
      state.image = image;
      state.error = undefined;
      state.isImageLoading = false;
    },
    getImage1dFailure(state, action: PayloadAction<{message: string}>) {
      const { message } = action.payload;
      state.image = undefined;
      state.error = message;
      state.isImageLoading = false;
    },
    getNumericalStart(state) {
      state.error = undefined;
      state.isNumericalLoading = true;
    },
    getNumericalSuccess(state, action: PayloadAction<{numerical: string[][]}>) {
      const { numerical } = action.payload;
      state.numerical = numerical;
      state.error = undefined;
      state.isNumericalLoading = false;
    },
    getNumericalFailure(state, action: PayloadAction<{message: string}>) {
      const { message } = action.payload;
      state.image = undefined;
      state.error = message;
      state.isNumericalLoading = false;
    },

  },
});

// Post.
export const fetchImage1d = (): AppThunk => async (dispatch, getState) => {
  const postData = selectPostData(getState());

  try {
    dispatch(getImage1dStart());

    const result = await request_imgfile1d_generation(postData);

    if (result == null) {
      throw new Error('Image file generation failed');
    }

    // Expects 'image/png' (successful) 'application/json' (unsuccessful)
    if (result.type.includes('json') || !result.type.includes('image') ) {
      const json = JSON.parse(await result.text());
      throw new Error(json.message);
    }

    dispatch(getImage1dSuccess({image: result}));
  } catch (error) {
    dispatch(getImage1dFailure({message: error.toString()}));
  }
};

export const fetchNumerical = (): AppThunk => async (dispatch, getState) => {
  const postData = selectPostData(getState());

  try {
    dispatch(getNumericalStart());

    const json = await receive_numeric1d(postData);

    if (json == null) {
      throw new Error('Numerical data generation failed');
    }

    if (!json.success) {
      throw new Error(json.message);
    }

    dispatch(getNumericalSuccess({numerical: json.table}));
  } catch (error) {
    dispatch(getNumericalFailure({message: error.toString()}));
  }
};

export const {
  // Spatial domain.
  updateLat,
  updateLon,

  // XY.
  updateXMax,
  updateXMin,
  updateYMax,
  updateYMin,
  updateYRangeEnabled,

  // Misc.
  updateImageSize,
  updatePeriodAverageEnabled,
  updateUnit,

  // Post.
  getImage1dStart,
  getImage1dSuccess,
  getImage1dFailure,
  getNumericalStart,
  getNumericalSuccess,
  getNumericalFailure,
} = viewer1dOptionSlice.actions;

export default viewer1dOptionSlice.reducer;

// Spatial domain.
export const selectLat = (state: RootState) => {
  const { lat } = state.viewer1dOption;
  return lat;
};

// XY.
export const selectXMin = (state: RootState) => {
  const { xMin } = state.viewer1dOption;
  return xMin;
};

export const selectXMax = (state: RootState) => {
  const { xMax } = state.viewer1dOption;
  return xMax;
};

export const selectYMin = (state: RootState) => {
  const { yMin } = state.viewer1dOption;
  return yMin;
};

export const selectYMax = (state: RootState) => {
  const { yMax } = state.viewer1dOption;
  return yMax;
};

export const selectYRangeEnabled = (state: RootState) => {
  const { yRangeEnabled } = state.viewer1dOption;
  return yRangeEnabled;
};

export const selectLon = (state: RootState) => {
  const { lon } = state.viewer1dOption;
  return lon;
};

// Misc.
export const selectImageSize = (state: RootState) => {
  const { imageSize } = state.viewer1dOption;
  return imageSize;
};

export const selectPeriodAverageEnabled = (state: RootState) => {
  const { periodAverageEnabled } = state.viewer1dOption;
  return periodAverageEnabled;
};

export const selectUnit = (state: RootState) => {
  const { unit } = state.viewer1dOption;
  return unit;
};

// Post.
export const selectError = (state: RootState) => {
  const { error } = state.viewer1dOption;
  return error;
};

export const selectImage1d = (state: RootState) => {
  const { image } = state.viewer1dOption;
  return image;
};

export const selectIsLoading = (state: RootState) => {
  const { isImageLoading, isNumericalLoading } = state.viewer1dOption;
  return isImageLoading || isNumericalLoading;
};

export const selectNumerical = (state: RootState) => {
  const { numerical } = state.viewer1dOption;
  return numerical;
};

// PostData.
const _selectPostData = (state: RootState) => {
  const {
    lat,
    lon,

    xMax,
    xMin,
    yMax,
    yMin,
    yRangeEnabled,

    imageSize,
    periodAverageEnabled,
    unit,
  } = state.viewer1dOption;

  const ret: {[prop: string]: unknown} = {
    lat,
    lon,

    xmax: xMax,
    xmin: xMin,
    ymax: yMax ?? null,
    ymin: yMin ?? null,
    usey: yRangeEnabled,

    size: imageSize,
    'period-average': periodAverageEnabled,
    unit,
  };

  return ret;
};

export const selectPostData = createSelector(
  _selectPostData,
  selectDatabase,
  selectEnsemble,
  selectExperiment,
  selectFrequency,
  selectModel,
  selectSingleVariable,
  (postData, database, ensemble, experiment, frequency, model, variable) => {
    return {
      ...postData,
      database,
      ensemble: ensemble ?? null,
      experiment: experiment ?? null,
      frequency,
      model,
      variable,
    };
  }
);
